import { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "AuthContext";
import { API_URL } from "config";






const mergeGrantedPermissions = (a = [], b = []) => [...new Set([...a, ...b])].filter(key => typeof key === "string");
const policyGrants = grants => Object.fromEntries(Object.entries({canViewPolicy:"ViewHRPolicy",canAddPolicy:"AddHRPolicy",canDeletePolicy:"DeleteHRPolicy",canTrashedPolicy:"TrashedHRPolicy",canRestorePolicy:"RestoreHRPolicy"}).map(([name,key])=>[name,grants.includes(key)||grants.includes("access.system.full_access")]));
const policyList = payload => { if (!payload?.success || !Array.isArray(payload.data)) throw new Error(payload?.message || "Invalid policy response"); return payload.data; };
const policySuccess = payload => { if (!payload?.success) throw new Error(payload?.message || "Policy action failed"); return payload; };
const policyUploadError = form => !form.title.trim() ? "Please enter policy title" : !form.document ? "Please upload a policy document" : "";
const isAbortError = error => error?.name === "AbortError";
async function authenticatedFetch(path, options = {}) {
  const token = localStorage.getItem("authToken");
  return fetch(`${API_URL}${path}`, { ...options, headers: { ...options.headers, Authorization: `Bearer ${token}` } });
}
async function jsonRequest(path, options) {
  const response = await authenticatedFetch(path, options), payload = await response.json();
  if (!response.ok) throw new Error(payload?.message || "Policy request failed");
  return payload;
}
const http = {
 getFresh: (path, signal) => jsonRequest(path, {signal}),
 post: (path, body, _multipart, {signal}) => jsonRequest(path, {method:"POST",body,signal}),
};
function createRequestScope() {
 const requests = new Map(); let disposed = false;
 return { activate(){disposed=false;}, begin(key){requests.get(key)?.abort(); const controller=new AbortController(); if(disposed)controller.abort(); requests.set(key,controller); return {signal:controller.signal,current:()=>!disposed&&!controller.signal.aborted&&requests.get(key)===controller};}, cancel(key){requests.get(key)?.abort();requests.delete(key);}, dispose(){disposed=true;for(const request of requests.values())request.abort();requests.clear();} };
}
function useActorScope(extra) {
 const auth=useAuth();
 const signature=JSON.stringify([auth.isAuthReady,auth.user,auth.roles,auth.permissions,auth.userPermission,auth.accessScopes,auth.accessUserScope,localStorage.getItem("authToken"),extra]);
 const scope=useMemo(()=>createRequestScope(),[signature]);
 useEffect(()=>{scope.activate();return()=>scope.dispose();},[scope]);
 return scope;
}

const emptyForm = () => ({ title: "", description: "", document: null, branch_id: "", subbranch_id: "", shift_id: "", user_id: "" });
const emptyAction = () => ({ open: false, type: "delete", policy: null, reason: "" });
const initialState = scope => ({ scope, policies: [], audience: null, page: 1, perPage: 10, pagination: { total: 0, current_page: 1, per_page: 10, last_page: 1 }, loading: true, submitting: false, viewingId: null, modalOpen: false, actionDialog: emptyAction(), form: emptyForm(), snackbar: { open: false, message: "", color: "success" } });
async function responseError(response, fallback) {
  const body = await response.json().catch(() => ({}));
  throw new Error(body.message || fallback);
}

export default function usePolicy(isTrashed) {
  const { user, permissions, userPermission, isAuthReady } = useAuth();
  const scope = useActorScope(String(isTrashed));
  const mutationBusy = useMemo(() => ({ value: false }), [scope]);
  const signature = JSON.stringify(mergeGrantedPermissions(permissions, userPermission).sort());
  const grants = useMemo(() => policyGrants(JSON.parse(signature)), [signature]);
  const available = isAuthReady && Boolean(user);
  const allowed = Object.fromEntries(Object.entries(grants).map(([key, value]) => [key, available && value]));
  const canRead = isTrashed ? allowed.canTrashedPolicy : allowed.canViewPolicy;
  const [state, update] = useState(() => initialState(scope));
  const current = state.scope === scope ? state : initialState(scope);
  const setCurrent = useCallback(change => update(previous => {
    const value = previous.scope === scope ? previous : initialState(scope);
    return { ...value, ...(typeof change === "function" ? change(value) : change) };
  }), [scope]);
  const showMessage = useCallback((message, color = "success") => setCurrent({ snackbar: { open: true, message, color } }), [setCurrent]);
  const fetchPolicies = useCallback(async () => {
    if (!canRead) return;
    const request = scope.begin("list");
    setCurrent({ loading: true });
    try {
      const endpoint = isTrashed ? "/hr-policies/trashed/list" : "/hr-policies";
      const payload = await http.getFresh(`${endpoint}?page=${current.page}&per_page=${current.perPage}`, request.signal);
      if (request.current()) setCurrent({ policies: policyList(payload), pagination: { total: payload.total ?? payload.data.length, current_page: payload.current_page ?? 1, per_page: payload.per_page ?? 10, last_page: payload.last_page ?? 1 }, loading: false });
    } catch (error) {
      if (!request.current() || isAbortError(error)) return;
      setCurrent({ policies: [], loading: false });
      showMessage(error.message || "Failed to load HR policies", "error");
    }
  }, [canRead, isTrashed, current.page, current.perPage, scope, setCurrent, showMessage]);
  useEffect(() => { setCurrent(initialState(scope)); }, [scope, setCurrent]);
  useEffect(() => { fetchPolicies(); return () => scope.cancel("list"); }, [scope, fetchPolicies]);
  const setForm = useCallback(change => { if (allowed.canAddPolicy && !isTrashed) setCurrent(previous => ({ form: typeof change === "function" ? change(previous.form) : change })); }, [allowed.canAddPolicy, isTrashed, setCurrent]);
  const setModalOpen = useCallback(open => { if (allowed.canAddPolicy && !isTrashed) setCurrent({ modalOpen: open }); }, [allowed.canAddPolicy, isTrashed, setCurrent]);
  const setSnackbar = useCallback(change => setCurrent(previous => ({ snackbar: typeof change === "function" ? change(previous.snackbar) : change })), [setCurrent]);
  const setActionDialog = useCallback(change => setCurrent(previous => ({ actionDialog: typeof change === "function" ? change(previous.actionDialog) : change })), [setCurrent]);
  const closeModal = useCallback((force = false) => { if (!current.submitting || force === true) setCurrent({ modalOpen: false, form: emptyForm() }); }, [current.submitting, setCurrent]);
  const openActionDialog = useCallback((type, policy) => {
    if (!canRead || (type === "delete" ? isTrashed || !allowed.canDeletePolicy : !isTrashed || !allowed.canRestorePolicy)) return;
    setCurrent({ actionDialog: { open: true, type, policy, reason: "" } });
  }, [canRead, isTrashed, allowed.canDeletePolicy, allowed.canRestorePolicy, setCurrent]);
  const closeActionDialog = useCallback(() => { if (!current.submitting) setCurrent({ actionDialog: emptyAction() }); }, [current.submitting, setCurrent]);

  const audienceQuery = new URLSearchParams({ branch_id: current.form.branch_id, subbranch_id: current.form.subbranch_id, shift_id: current.form.shift_id }).toString();
  const audienceReady = current.audience?.query === audienceQuery && !current.audience?.error;
  useEffect(() => {
    if (!current.modalOpen || !allowed.canAddPolicy || isTrashed) return;
    const request = scope.begin("audience");
    setCurrent({ audience: null });
    http.getFresh(`/hr-policies/audience-options?${audienceQuery}`, request.signal).then(payload => {
      if (!payload?.success || !payload?.data || !["branch_id", "subbranch_id", "shift_id", "user_id"].every(key => Array.isArray(payload.data[key]))) throw new Error("Unable to load policy audience");
      if (request.current()) setCurrent({ audience: { query: audienceQuery, data: payload.data } });
    }).catch(error => {
      if (request.current() && !isAbortError(error)) setCurrent({ audience: { query: audienceQuery, error: error.message || "Unable to load policy audience" } });
    });
    return () => scope.cancel("audience");
  }, [current.modalOpen, allowed.canAddPolicy, isTrashed, scope, audienceQuery, setCurrent]);
  const setAudienceField = useCallback((key, value) => {
    if (current.submitting || !allowed.canAddPolicy || isTrashed) return;
    setForm(previous => ({ ...previous, [key]: value,
      ...(key === "branch_id" ? { subbranch_id: "", shift_id: "", user_id: "" } : {}),
      ...(key === "subbranch_id" ? { shift_id: "", user_id: "" } : {}),
      ...(key === "shift_id" ? { user_id: "" } : {}) }));
  }, [current.submitting, allowed.canAddPolicy, isTrashed, setForm]);

  const handleSubmit = useCallback(async () => {
    if (!allowed.canAddPolicy || isTrashed || current.submitting || mutationBusy.value || !audienceReady) return;
    const error = policyUploadError(current.form);
    if (error) { showMessage(error, "error"); return; }
    mutationBusy.value = true;
    const request = scope.begin("mutation"), payload = new FormData();
    payload.append("title", current.form.title.trim());
    payload.append("description", current.form.description.trim());
    payload.append("document", current.form.document);
    for (const key of ["branch_id", "subbranch_id", "shift_id", "user_id"]) payload.append(key, current.form[key]);
    setCurrent({ submitting: true });
    try {
      policySuccess(await http.post("/hr-policies", payload, true, { signal: request.signal }));
      if (!request.current()) return;
      setCurrent({ modalOpen: false, form: emptyForm() });
      showMessage("Policy uploaded successfully");
      await fetchPolicies();
    } catch (error) { if (request.current() && !isAbortError(error)) showMessage(error.message || "Policy upload failed", "error"); }
    finally { mutationBusy.value = false; if (request.current()) setCurrent({ submitting: false }); }
  }, [allowed.canAddPolicy, isTrashed, audienceReady, current.submitting, current.form, scope, mutationBusy, setCurrent, showMessage, fetchPolicies]);

  const handleViewDocument = useCallback(async (policy, downloadOnly = false) => {
    if (!allowed.canViewPolicy || isTrashed) return;
    // Open within the user's click so popup blockers do not discard the preview.
    const preview = downloadOnly ? null : window.open("", "_blank");
    if (preview) preview.opener = null;
    const request = scope.begin("document");
    const onAbort = () => preview?.close();
    request.signal.addEventListener("abort", onAbort, { once: true });
    setCurrent({ viewingId: policy.id });
    try {
      const response = await authenticatedFetch(`/hr-policies/${policy.id}/view`, { signal: request.signal, headers: { Accept: "*/*" } });
      if (!response.ok) await responseError(response, "Unable to open document");
      const blob = await response.blob();
      if (!request.current()) { preview?.close(); return; }
      const url = URL.createObjectURL(blob);
      if (preview) {
        preview.location.replace(url);
        const release = window.setInterval(() => { if (preview.closed || request.signal.aborted) { if (request.signal.aborted) preview.close(); URL.revokeObjectURL(url); window.clearInterval(release); } }, 1000);
      } else {
        const link = document.createElement("a"); link.href = url; link.download = policy.original_filename || "Document"; link.rel = "noopener"; link.click();
        window.setTimeout(() => URL.revokeObjectURL(url), 0);
      }
    } catch (error) { preview?.close(); if (request.current() && !isAbortError(error)) showMessage(error.message || "Unable to open document", "error"); }
    finally { request.signal.removeEventListener("abort", onAbort); if (request.current()) setCurrent({ viewingId: null }); }
  }, [allowed.canViewPolicy, isTrashed, scope, setCurrent, showMessage]);

  const handleConfirmAction = useCallback(async () => {
    const action = current.actionDialog;
    if (!canRead || current.submitting || mutationBusy.value || !action.policy || (action.type === "delete" ? isTrashed || !allowed.canDeletePolicy : !isTrashed || !allowed.canRestorePolicy)) return;
    if (!action.reason.trim()) { showMessage("Please enter reason", "error"); return; }
    mutationBusy.value = true;
    const request = scope.begin("mutation");
    setCurrent({ submitting: true });
    try {
      const endpoint = `/hr-policies/${action.policy.id}${action.type === "delete" ? "" : "/restore"}`;
      const response = await authenticatedFetch(endpoint, { method: action.type === "delete" ? "DELETE" : "POST", signal: request.signal, headers: { "Content-Type": "application/json" }, body: JSON.stringify({ reason: action.reason.trim() }) });
      if (!response.ok) await responseError(response, "Action failed");
      policySuccess(await response.json());
      if (!request.current()) return;
      setCurrent({ actionDialog: emptyAction() });
      showMessage(action.type === "delete" ? "Policy deleted successfully" : "Policy restored successfully");
      await fetchPolicies();
    } catch (error) { if (request.current() && !isAbortError(error)) showMessage(error.message || "Action failed", "error"); }
    finally { mutationBusy.value = false; if (request.current()) setCurrent({ submitting: false }); }
  }, [canRead, current.actionDialog, current.submitting, isTrashed, allowed.canDeletePolicy, allowed.canRestorePolicy, scope, mutationBusy, setCurrent, showMessage, fetchPolicies]);

  return { ...allowed, pagination: current.pagination, setPage: page => setCurrent({ page, policies: [], loading: true }), setPerPage: perPage => setCurrent({ perPage, page: 1, policies: [], loading: true }), handleDownloadDocument: policy => handleViewDocument(policy, true), audience: audienceReady ? current.audience.data : {}, audienceReady, audienceError: current.audience?.error, setAudienceField, policies: canRead ? current.policies : [], loading: canRead && current.loading, submitting: current.submitting, viewingId: current.viewingId, modalOpen: allowed.canAddPolicy && !isTrashed && current.modalOpen, actionDialog: canRead ? current.actionDialog : emptyAction(), form: current.form, snackbar: current.snackbar, setSnackbar, setForm, setModalOpen, setActionDialog, closeModal, openActionDialog, closeActionDialog, handleSubmit, handleViewDocument, handleConfirmAction };
}
